import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, LogOut, User as UserIcon, Zap } from 'lucide-react';

declare global {
    interface Window {
        google?: any;
    }
}

export default function GlobalHeader() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const location = useLocation();
    const { config } = useBrand();
    const { user, login, logout } = useAuth();

    useEffect(() => {
        const GOOGLE_CLIENT_ID = (import.meta as any).env.VITE_GOOGLE_CLIENT_ID || "";
        if (isLoginModalOpen && window.google && GOOGLE_CLIENT_ID) {
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: async (response: any) => {
                    await login(response.credential);
                    setIsLoginModalOpen(false);
                }
            });
            window.google.accounts.id.renderButton(
                document.getElementById("google-signin-btn"),
                { theme: "filled_blue", size: "large", width: 280, shape: "pill" }
            );
        }
    }, [isLoginModalOpen, login]);

    const navItems = [
        { label: 'Diagnose', path: '/chat' },
        { label: 'Parts', path: '/parts' },
        { label: 'Diagrams', path: '/diagrams' },
        { label: 'AR', path: '/ar' },
        { label: 'Projects', path: '/projects' },
    ];

    const brandName = config?.name || 'DIYCarMech.com™';

    return (
        <header className="border-b border-slate/30 bg-dark/80 backdrop-blur-md sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                <Link to="/" className="group">
                    <h1 className="text-2xl font-black bg-gradient-to-r from-pink-400 to-primary bg-clip-text text-transparent tracking-tighter">
                        {brandName}
                    </h1>
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold group-hover:text-primary transition-colors">
                        Universal Interface
                    </p>
                </Link>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center gap-8">
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`text-xs uppercase tracking-[0.2em] font-bold transition-all hover:text-primary ${location.pathname === item.path ? 'text-primary' : 'text-gray-400'
                                }`}
                        >
                            {item.label}
                        </Link>
                    ))}
                    <Link
                        to="/upgrade"
                        className="text-xs uppercase tracking-[0.2em] font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-300 hover:to-yellow-500 transition-all flex items-center gap-1"
                    >
                        <Zap size={12} className="text-yellow-500" />
                        Upgrade
                    </Link>

                    {user ? (
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 bg-slate/10 px-3 py-1.5 rounded-full border border-slate/20">
                                {user.avatar_url ? (
                                    <img src={user.avatar_url} alt="" className="w-5 h-5 rounded-full" />
                                ) : (
                                    <UserIcon size={14} className="text-primary" />
                                )}
                                <span className="text-[10px] text-gray-300 font-bold uppercase truncate max-w-[100px]">{user.name || user.email}</span>
                            </div>
                            <button
                                onClick={logout}
                                className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                                title="Sign Out"
                            >
                                <LogOut size={16} />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setIsLoginModalOpen(true)}
                            className="bg-primary hover:bg-primary/90 text-dark px-6 py-2 rounded-full text-[10px] uppercase font-black tracking-widest shadow-lg shadow-primary/20 transition-all flex items-center gap-2"
                        >
                            <LogIn size={14} />
                            Sign In
                        </button>
                    )}
                </nav>

                {/* Mobile Toggle */}
                <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-gray-400">
                    {menuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Login Modal */}
            {isLoginModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-dark/90 backdrop-blur-xl animate-in fade-in zoom-in duration-300">
                    <div className="relative w-full max-w-md bg-dark border border-slate/20 rounded-[2.5rem] p-10 overflow-hidden shadow-2xl">
                        {/* Background Glow */}
                        <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/10 rounded-full blur-[80px]"></div>
                        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-secondary/10 rounded-full blur-[80px]"></div>

                        <button
                            onClick={() => setIsLoginModalOpen(false)}
                            className="absolute top-6 right-6 p-2 text-gray-500 hover:text-white transition-colors"
                        >
                            <X size={24} />
                        </button>

                        <div className="relative space-y-8 text-center">
                            <div className="flex flex-col items-center gap-4">
                                <div className="p-4 bg-primary/10 rounded-2xl">
                                    <Zap size={40} className="text-primary" />
                                </div>
                                <div className="space-y-2">
                                    <h2 className="text-3xl font-black text-white uppercase tracking-tighter">Initialize Core</h2>
                                    <p className="text-gray-400 text-sm">Sign in to sync projects, access Level 2 master mechanic data, and track diagnostic history.</p>
                                </div>
                            </div>

                            <div className="space-y-4 flex flex-col items-center">
                                {((import.meta as any).env.VITE_GOOGLE_CLIENT_ID) ? (
                                    <div id="google-signin-btn" className="min-h-[40px]"></div>
                                ) : (
                                    <div className="flex flex-col gap-2 w-full max-w-[280px]">
                                        <input
                                            type="email"
                                            placeholder="Enter test email..."
                                            className="px-4 py-2 rounded-full text-sm bg-slate/10 text-white placeholder-gray-500 border border-slate/30 focus:outline-none focus:border-primary"
                                            onKeyDown={async (e) => {
                                                if (e.key === 'Enter') {
                                                    const em = e.currentTarget.value;
                                                    if (em) {
                                                        // Fallback logic for local testing without proper Google auth
                                                        await login('test-token-bypass');
                                                        localStorage.setItem('diymech_user', JSON.stringify({ id: 'test-123', email: em, name: em.split('@')[0] }));
                                                        window.location.reload();
                                                    }
                                                }
                                            }}
                                        />
                                        <p className="text-[9px] text-gray-500 text-center">Press Enter to sign in (Dev Fallback)</p>
                                    </div>
                                )}
                                <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Secure Logic Pipeline Active</p>
                            </div>

                            <div className="pt-4 border-t border-white/5 grid grid-cols-2 gap-4 text-left">
                                <div className="space-y-1">
                                    <h4 className="text-[10px] text-white font-black uppercase tracking-widest">Persistence</h4>
                                    <p className="text-[9px] text-gray-500">Cross-device project synchronization</p>
                                </div>
                                <div className="space-y-1">
                                    <h4 className="text-[10px] text-white font-black uppercase tracking-widest">Intelligence</h4>
                                    <p className="text-[9px] text-gray-500">Access to all expert AI personas</p>
                                </div>
                            </div>

                            <div className="flex justify-center gap-6 pt-4">
                                <Link to="/privacy" onClick={() => setIsLoginModalOpen(false)} className="text-[10px] text-gray-600 hover:text-primary transition-colors uppercase font-bold tracking-widest">Privacy Policy</Link>
                                <Link to="/terms" onClick={() => setIsLoginModalOpen(false)} className="text-[10px] text-gray-600 hover:text-primary transition-colors uppercase font-bold tracking-widest">Terms of Service</Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile Menu */}
            {menuOpen && (
                <div className="md:hidden border-t border-slate/20 bg-dark p-6 space-y-4 animate-in fade-in slide-in-from-top-4">
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setMenuOpen(false)}
                            className="block text-sm font-bold text-gray-300 hover:text-primary"
                        >
                            {item.label}
                        </Link>
                    ))}
                </div>
            )}
        </header>
    );
}
